export const WORKFLOW_STATUS = {
  draft: 'Draft',
  published: 'Published',
} as const;

export type WorkflowFilterType = 'FilterCriteria';

export const WORKFLOW_FILTER_TYPE = {
  filterCriteria: 'FilterCriteria',
} as const;

export type WorkflowActionType = 'OrderRuleAction';

export const WORKFLOW_ACTION_TYPE = {
  orderRuleAction: 'OrderRuleAction',
} as const;

export const WORKFLOW_RULE_REFERENCE_TYPE = {
  workflowRule: 'AutomationWorkflowRule',
} as const;

export type StringFilter = {
  field: string;
  operator: string;
  value: string;
};

export type NumericFilter = {
  field: string;
  operator: string;
  value: string;
};

export type DateTimeFilter = {
  field: string;
  operator: string;
  value: string;
};

export type CollectionFilter = {
  field: string;
  operator: string;
  values: string[];
};

export type BooleanApiFilter = {
  field: string;
  value: boolean;
};

export type AppliedFilters = {
  stringFilters?: StringFilter[];
  numericFilters?: NumericFilter[];
  dateTimeFilters?: DateTimeFilter[];
  collectionFilters?: CollectionFilter[];
  booleanFilters?: BooleanApiFilter[];
};

export type WorkflowFilter = {
  type: typeof WORKFLOW_FILTER_TYPE.filterCriteria;
  definition: AppliedFilters;
};

export type WorkflowAction = {
  type: typeof WORKFLOW_ACTION_TYPE.orderRuleAction;
  definition: {
    actionTypeId: number;
    parameter: string | null;
    intParameter: number | null;
  };
};

export type WorkflowRulePayload = {
  automationWorkflowRuleName: string;
  description: string;
  entityType: string;
  filters: WorkflowFilter[];
  actions: WorkflowAction[];
};

export type WorkflowRule = WorkflowRulePayload & {
  automationWorkflowRuleId: number;
  isActive: boolean;
  createdUtc: string;
  modifiedUtc: string;
};

export type TriggerFilterCriterion = {
  field: string;
  operator: string;
  value: string | number;
  valueType: string;
};

// Used in create/update workflow requests
export type WorkflowTrigger = {
  automationWorkflowTriggerId?: number;
  automationWorkflowTriggerName: string;
  eventType: string;
  eventFilter: { criteria: TriggerFilterCriterion[] } | null;
  isActive?: boolean;
};

// Returned by the API in full workflow responses — ID is always present
export type WorkflowTriggerResponse = Omit<WorkflowTrigger, 'automationWorkflowTriggerId' | 'isActive'> & {
  automationWorkflowTriggerId: number;
  automationWorkflowId: number;
  isActive: boolean;
  createdUtc: string;
  modifiedUtc: string;
};

export type WorkflowStatus = (typeof WORKFLOW_STATUS)[keyof typeof WORKFLOW_STATUS];

export type WorkflowRuleReference = {
  type: typeof WORKFLOW_RULE_REFERENCE_TYPE.workflowRule;
  definition: {
    id: number;
    status: WorkflowStatus;
  };
};

// Request body for create/update workflow
export type WorkflowPayload = {
  automationWorkflowName: string;
  description: string;
  entityType: string;
  rules: WorkflowRuleReference[];
  triggers: WorkflowTrigger[];
  automationWorkflowStatus: WorkflowStatus;
};

// Returned by create/update/publish — rule references only, no triggers
export type WorkflowResponse = {
  automationWorkflowId: number;
  automationWorkflowName: string;
  description: string;
  entityType: string;
  rules: WorkflowRuleReference[];
  automationWorkflowStatus: WorkflowStatus;
  createdUtc: string;
  modifiedUtc: string;
};

// Returned by list/get — full embedded rules and triggers
export type Workflow = {
  automationWorkflowId: number;
  automationWorkflowName: string;
  description: string;
  entityType: string;
  automationWorkflowStatus: WorkflowStatus;
  createdUtc: string;
  modifiedUtc: string;
  rules: WorkflowRule[];
  triggers: WorkflowTriggerResponse[];
};
